// FamilyBalances.js
// -----------------------------------------------------------------------------
// Family list with outstanding balances aggregated across every owed module
// (afterschool, bus, activities, daycare, art school — colonie excluded).
// "View Statement" opens FamilyStatementModal instead of replacing the screen.
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useMemo } from 'react';
import { Search, DollarSign, Users, FileText } from 'lucide-react';
import FamilyStatementModal from './modals/FamilyStatementModal';
import {
  BALANCE_MODULES,
  fetchModuleEnrollments,
  formatMoney as fmt,
} from './balanceModules';

const FamilyBalances = ({ apiCall, selectedYear, currentAcademicYear, hasRole, showSuccess, showError }) => {
  const [families, setFamilies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statementFamily, setStatementFamily] = useState(null); // drives the modal
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFamilies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const loadFamilies = async () => {
    try {
      setLoading(true);
      const familyMap = new Map();

      // School-year modules follow the selected year; Daycare & Art School are
      // continuous, so they use the ACTIVE year and surface their families
      // regardless of which school year is selected.
      const CONTINUOUS = new Set(['daycare', 'artschool']);
      const continuousYear = currentAcademicYear || selectedYear;

      // Pull every owed module in parallel. Colonie is excluded because it isn't
      // in BALANCE_MODULES. Charge-based modules (daycare) return per-family
      // totals; the rest return enrollment rows we normalize.
      const moduleResults = await Promise.all(
        BALANCE_MODULES.map(async (m) => {
          const yr = CONTINUOUS.has(m.key) ? continuousYear : selectedYear;
          if (m.loadFamilyTotals) {
            return { module: m, kind: 'totals', data: await m.loadFamilyTotals(apiCall, { academicYear: yr }) };
          }
          return { module: m, kind: 'enrollments', data: await fetchModuleEnrollments(apiCall, m, { academicYear: yr }) };
        })
      );

      // Get (or create) a family entry, backfilling identity fields as we go.
      const ensureFam = (id, src) => {
        if (!familyMap.has(id)) {
          familyMap.set(id, {
            FamilyID: id,
            FatherName: src.FatherName || src.FamilyDisplayName || '',
            MotherName: src.MotherName || '',
            PrimaryPhone: src.PrimaryPhone || '',
            TotalOutstanding: 0,
          });
        }
        const fam = familyMap.get(id);
        if (!fam.FatherName) fam.FatherName = src.FatherName || src.FamilyDisplayName || '';
        if (!fam.MotherName && src.MotherName) fam.MotherName = src.MotherName;
        if (!fam.PrimaryPhone && src.PrimaryPhone) fam.PrimaryPhone = src.PrimaryPhone;
        return fam;
      };

      // Base list = families registered at the school for the SELECTED year, so
      // even a 0-balance registered family shows (its real tuition lives in SIS,
      // pulled when the statement is printed — temporary, until tuition moves
      // into the web app). Continuous Daycare/Art-School families are added by
      // their modules below regardless of school registration.
      try {
        const regs = await apiCall(`/registration?academicYear=${encodeURIComponent(selectedYear)}`);
        (Array.isArray(regs) ? regs : []).forEach((r) => {
          if (r && r.FamilyID) ensureFam(r.FamilyID, r);
        });
      } catch (_) { /* no registration access / none for this year — rely on module families */ }

      moduleResults.forEach(({ module, kind, data }) => {
        if (kind === 'totals') {
          data.forEach((f) => {
            if (!f || !f.FamilyID) return;
            ensureFam(f.FamilyID, f).TotalOutstanding += f.balance || 0;
          });
        } else {
          data.forEach((enrollment) => {
            if (!enrollment || !enrollment.FamilyID) return;
            const fam = ensureFam(enrollment.FamilyID, enrollment);
            // Use the SAME normalizer as the statement so totals always agree.
            if (enrollment.Status === 'Active') {
              fam.TotalOutstanding += module.normalize(enrollment).balance || 0;
            }
          });
        }
      });

      // Owing families first (so the 0-balance majority doesn't bury them),
      // then alphabetical.
      const list = Array.from(familyMap.values()).sort((a, b) => {
        const diff = (b.TotalOutstanding || 0) - (a.TotalOutstanding || 0);
        return diff !== 0 ? diff : (a.FatherName || '').localeCompare(b.FatherName || '');
      });
      setFamilies(list);
    } catch (error) {
      showError('Error loading families: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredFamilies = useMemo(() => {
    if (!searchTerm) return families;
    const q = searchTerm.toLowerCase();
    return families.filter(
      (f) =>
        String(f.FamilyID).toLowerCase().includes(q) ||
        f.FatherName.toLowerCase().includes(q) ||
        (f.MotherName && f.MotherName.toLowerCase().includes(q)) ||
        (f.PrimaryPhone && f.PrimaryPhone.includes(searchTerm))
    );
  }, [searchTerm, families]);

  const totalOutstanding = useMemo(
    () => families.reduce((s, f) => s + (f.TotalOutstanding > 0 ? f.TotalOutstanding : 0), 0),
    [families]
  );
  const familiesOwing = useMemo(
    () => families.filter((f) => f.TotalOutstanding > 0).length,
    [families]
  );

  if (loading) {
    return <div className="text-center py-8">Loading families...</div>;
  }

  return (
    <div>
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Family Balances - {selectedYear}</h1>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by Family ID, Name, or Phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <Users className="text-blue-600" size={24} />
              <span className="text-sm text-gray-600">Total Families</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{families.length}</p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <Users className="text-red-600" size={24} />
              <span className="text-sm text-gray-600">Families with Balance Due</span>
            </div>
            <p className="text-2xl font-bold text-red-600">{familiesOwing}</p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="text-orange-600" size={24} />
              <span className="text-sm text-gray-600">Total Outstanding</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">{fmt(totalOutstanding)}</p>
          </div>
        </div>

        {/* Family list */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Family ID</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Family Name</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Phone</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Outstanding Balance</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFamilies.map((family) => (
                <tr key={family.FamilyID} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-sm">{family.FamilyID}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{family.FatherName}</div>
                    {family.MotherName && (
                      <div className="text-sm text-gray-600">{family.MotherName}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{family.PrimaryPhone}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`font-bold ${
                        family.TotalOutstanding > 0
                          ? 'text-red-600'
                          : family.TotalOutstanding < 0
                          ? 'text-green-600'
                          : 'text-gray-600'
                      }`}
                    >
                      {fmt(Math.abs(family.TotalOutstanding))}
                      {family.TotalOutstanding < 0 && ' (Credit)'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setStatementFamily(family)}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-semibold text-sm"
                    >
                      <FileText size={16} /> View Statement
                    </button>
                  </td>
                </tr>
              ))}
              {filteredFamilies.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    No families found matching "{searchTerm}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Statement modal */}
      {statementFamily && (
        <FamilyStatementModal
          apiCall={apiCall}
          family={statementFamily}
          academicYear={selectedYear}
          onClose={() => setStatementFamily(null)}
          showError={showError}
        />
      )}
    </div>
  );
};

export default FamilyBalances;
